import type { CSSProperties, ReactNode } from "react";
import { Marked, type Token, type Tokens } from "marked";
import { Cmarkdownheading } from "../Cmarkdownheading/Cmarkdownheading";
import { Cmarkdownparagraph } from "../Cmarkdownparagraph/Cmarkdownparagraph";
import { Cmarkdownblockquote } from "../Cmarkdownblockquote/Cmarkdownblockquote";
import { Cmarkdownlist } from "../Cmarkdownlist/Cmarkdownlist";
import { Cmarkdownlistitem } from "../Cmarkdownlistitem/Cmarkdownlistitem";
import { Cmarkdowncode } from "../Cmarkdowncode/Cmarkdowncode";
import { Cmarkdownmath } from "../Cmarkdownmath/Cmarkdownmath";
import { Cmarkdownimage } from "../Cmarkdownimage/Cmarkdownimage";
import { Cmarkdownpiechart } from "../Cmarkdownpiechart/Cmarkdownpiechart";
import { Cmarkdownflowchart } from "../Cmarkdownflowchart/Cmarkdownflowchart";
import { Cmarkdownxychart } from "../Cmarkdownxychart/Cmarkdownxychart";
import { Cmarkdownmindmap } from "../Cmarkdownmindmap/Cmarkdownmindmap";
import { Cmarkdowngantt } from "../Cmarkdowngantt/Cmarkdowngantt";
import { Cmarkdowntimeline } from "../Cmarkdowntimeline/Cmarkdowntimeline";
import { Cmarkdownkanban } from "../Cmarkdownkanban/Cmarkdownkanban";
import { Cmarkdownsequence } from "../Cmarkdownsequence/Cmarkdownsequence";
import { Cmarkdowntreemap } from "../Cmarkdowntreemap/Cmarkdowntreemap";
import { Cmarkdownquadrant } from "../Cmarkdownquadrant/Cmarkdownquadrant";
import { Cmarkdownmap } from "../Cmarkdownmap/Cmarkdownmap";
import { parseAttachResults } from "@cabal/shared/domain/attached-result/index.js";
import { attachedResultCardFor } from "../Cattachedresult/cattachedresult.registry";
import { Cmarkdownhr } from "../Cmarkdownhr/Cmarkdownhr";
import {
  Cmarkdowntable,
  type CmarkdowntableCell,
  type CmarkdowntableTextAlign,
} from "../Cmarkdowntable/Cmarkdowntable";
import { FontWeight } from "@/theme/typography";
import { MATH_BLOCK_TOKEN_NAME, isMathBlockToken } from "./math-types";
import { ParenListBlock } from "./ParenListBlock";
import { PAREN_LIST_TOKEN_NAME, isParenListToken } from "./paren-list-types";
// The token table below is built at module scope, so the enum keying it must
// come from the import-free module rather than through the inline renderer,
// which shares this file's import cycle.
import { MarkdownTokenType } from "./markdown-token-type";
import {
  renderInlineContent,
  renderInlineTokens,
} from "./markdown-inline-renderer";
import { startsWithHebrew } from "./markdown-hebrew";
import { omitTrailingHorizontalRuleTokens } from "./omit-trailing-horizontal-rule";
import {
  MERMAID_FENCE_HEADER_ORDER,
  MermaidVisualForm,
  type MermaidFenceHeaderEntry,
} from "./mermaid-visual-patterns";
import {
  MarkdownTokenEvidence,
  MarkdownTokenEvidenceLevel,
  MarkdownTokenEvidenceState,
} from "./MarkdownTokenEvidence";

const MAX_MARKDOWN_DEPTH = 4;

enum MarkdownFenceLanguage {
  Markdown = "markdown",
  Md = "md",
}

enum MarkdownCodeLanguage {
  Mermaid = "mermaid",
  Map = "map",
  AttachedResults = "no-edit-data-block",
  Text = "text",
}

type MarkdownTableAlignment = CmarkdowntableTextAlign | null;

export interface RenderMarkdownFenceInput {
  key: string;
  content: string;
}

export interface MarkdownRenderOptions {
  textSize?: number;
  textWeight?: FontWeight;
  textAlign?: "left" | "center" | "right";
  blockquoteContentWrapperStyle?: CSSProperties;
  blockquoteContainerStyle?: CSSProperties;
  listStyle?: CSSProperties;
  listItemStyle?: CSSProperties;
  paragraphStyle?: CSSProperties;
  paragraphTextStyle?: CSSProperties;
  concaved?: boolean;
  /** Emit raw-token evidence only for an explicitly instrumented render. */
  evidenceEnabled: boolean;
  /**
   * Drop a trailing thematic break (and surrounding space tokens). Used by
   * accordion section bodies so the markdown HR does not stack on the row divider.
   */
  omitTrailingHorizontalRule?: boolean;
  renderMarkdownFence: (input: RenderMarkdownFenceInput) => ReactNode;
}

interface RenderMarkdownTokensInput {
  tokens: Token[];
  markdownParser: Marked;
  options: MarkdownRenderOptions;
  depth?: number;
}

interface RenderMarkdownTokenInput {
  token: Token;
  key: string;
  markdownParser: Marked;
  options: MarkdownRenderOptions;
  depth: number;
}

interface RenderListItemInput {
  item: Tokens.ListItem;
  itemIndex: number;
  keyPrefix: string;
  ordered: boolean;
  /** Marked list `start` — preserved so `5. Title` paints as 5, not always 1. */
  listStart: number;
  markdownParser: Marked;
  options: MarkdownRenderOptions;
  depth: number;
}

interface RenderCodeVisualInput {
  key: string;
  content: string;
}

interface RenderCodeBlockInput {
  codeToken: Tokens.Code;
  key: string;
  options: MarkdownRenderOptions;
}

interface ResolvedCodeBlockInput extends RenderCodeBlockInput {
  codeText: string;
  codeLang: string;
}

interface MermaidCodeBlockRenderer {
  pattern: RegExp;
  render: (input: RenderCodeVisualInput) => ReactNode;
}

type MarkdownTokenRenderer = (input: RenderMarkdownTokenInput) => ReactNode;
type CodeBlockCandidateRenderer = (
  input: ResolvedCodeBlockInput,
) => ReactNode | null;

export function renderMarkdownTokens(
  input: RenderMarkdownTokensInput,
): ReactNode[] {
  const depth = input.depth ?? 0;
  if (depth >= MAX_MARKDOWN_DEPTH) {
    return input.tokens.map((token, index) => (
      <MarkdownTokenEvidence
        enabled={input.options.evidenceEnabled}
        key={`depth-limit-${String(index)}-${String(depth)}`}
        level={MarkdownTokenEvidenceLevel.Block}
        source={markdownTokenSource(token)}
        state={MarkdownTokenEvidenceState.Dropped}
        tokenType={token.type}
      >
        {null}
      </MarkdownTokenEvidence>
    ));
  }

  const tokens =
    depth === 0 && input.options.omitTrailingHorizontalRule
      ? omitTrailingHorizontalRuleTokens({ tokens: input.tokens })
      : input.tokens;

  return tokens.map((token, index) => {
    const key = `${token.type}-${String(index)}-${String(depth)}`;
    const renderedToken = renderMarkdownToken({
      token,
      key,
      markdownParser: input.markdownParser,
      options: input.options,
      depth,
    });
    return (
      <MarkdownTokenEvidence
        enabled={input.options.evidenceEnabled}
        key={`${key}-evidence`}
        level={MarkdownTokenEvidenceLevel.Block}
        source={markdownTokenSource(token)}
        state={blockTokenEvidenceState({ renderedToken, token })}
        tokenType={token.type}
      >
        {renderedToken}
      </MarkdownTokenEvidence>
    );
  });
}

function markdownTokenSource(token: Token): string {
  if ("raw" in token && typeof token.raw === "string") return token.raw;
  if ("text" in token && typeof token.text === "string") return token.text;
  return "";
}

function blockTokenEvidenceState(input: {
  readonly renderedToken: ReactNode;
  readonly token: Token;
}): MarkdownTokenEvidenceState {
  if (
    input.renderedToken !== null &&
    input.renderedToken !== undefined &&
    input.renderedToken !== false
  ) {
    return MarkdownTokenEvidenceState.Rendered;
  }
  if (input.token.type === MarkdownTokenType.Space) {
    return MarkdownTokenEvidenceState.NonSemantic;
  }
  if (input.token.type === MarkdownTokenType.Code) {
    const codeToken = input.token as Tokens.Code;
    if (
      isEmptyCodeFence({
        codeText: codeToken.text ?? "",
        codeLang: getCodeLanguage(codeToken),
      })
    ) {
      return MarkdownTokenEvidenceState.NonSemantic;
    }
  }
  return MarkdownTokenEvidenceState.Dropped;
}

function renderMarkdownToken(input: RenderMarkdownTokenInput): ReactNode {
  const renderer = MARKDOWN_TOKEN_RENDERERS[input.token.type];
  if (!renderer) {
    return renderMarkdownTokenFallback(input);
  }

  try {
    return renderer(input);
  } catch {
    return renderMarkdownTokenFallback(input);
  }
}

/**
 * A specialized visual may fail, but its source text is still user data. The
 * old `null` fallback silently deleted an entire paragraph or list from the
 * rendered answer. Keep the exact token source visible, including line breaks,
 * so one malformed child can degrade presentation without erasing content.
 */
function renderMarkdownTokenFallback(
  input: RenderMarkdownTokenInput,
): ReactNode {
  const tokenText =
    "text" in input.token && typeof input.token.text === "string"
      ? input.token.text
      : "raw" in input.token && typeof input.token.raw === "string"
        ? input.token.raw
        : null;
  if (tokenText == null || tokenText.length === 0) return null;

  return (
    <Cmarkdownparagraph
      key={`${input.key}-fallback`}
      textSize={input.options.textSize}
      textWeight={input.options.textWeight}
      textAlign={input.options.textAlign}
      style={input.options.paragraphStyle}
      textStyle={{
        ...input.options.paragraphTextStyle,
        whiteSpace: "pre-wrap",
      }}
      hebrew={startsWithHebrew(tokenText)}
    >
      {tokenText}
    </Cmarkdownparagraph>
  );
}

const MARKDOWN_TOKEN_RENDERERS: Record<string, MarkdownTokenRenderer> = {
  [MarkdownTokenType.Heading]: renderHeadingToken,
  [MarkdownTokenType.Paragraph]: renderParagraphToken,
  [MarkdownTokenType.Blockquote]: renderBlockquoteToken,
  [MarkdownTokenType.List]: renderListToken,
  [MarkdownTokenType.Code]: renderCodeToken,
  [MATH_BLOCK_TOKEN_NAME]: renderMathBlockToken,
  [PAREN_LIST_TOKEN_NAME]: renderParenListToken,
  [MarkdownTokenType.HorizontalRule]: renderHorizontalRuleToken,
  [MarkdownTokenType.Image]: renderImageToken,
  [MarkdownTokenType.Table]: renderTableToken,
  [MarkdownTokenType.Space]: renderSpaceToken,
  [MarkdownTokenType.Text]: renderTextBlockToken,
  [MarkdownTokenType.HTML]: renderMarkdownTokenFallback,
};

function renderHeadingToken(input: RenderMarkdownTokenInput): ReactNode {
  const headingToken = input.token as Tokens.Heading;
  return (
    <Cmarkdownheading
      key={input.key}
      level={headingToken.depth as 1 | 2 | 3 | 4 | 5 | 6}
      textAlign={input.options.textAlign}
      hebrew={startsWithHebrew(headingToken.text)}
    >
      {headingToken.tokens
        ? renderInlineTokens({
            tokens: headingToken.tokens,
            markdownParser: input.markdownParser,
            evidenceEnabled: input.options.evidenceEnabled,
          })
        : headingToken.text}
    </Cmarkdownheading>
  );
}

function renderParagraphToken(input: RenderMarkdownTokenInput): ReactNode {
  const paragraphToken = input.token as Tokens.Paragraph;
  return (
    <Cmarkdownparagraph
      key={input.key}
      textSize={input.options.textSize}
      textWeight={input.options.textWeight}
      textAlign={input.options.textAlign}
      style={input.options.paragraphStyle}
      textStyle={input.options.paragraphTextStyle}
      hebrew={startsWithHebrew(paragraphToken.text)}
    >
      {paragraphToken.tokens
        ? renderInlineTokens({
            tokens: paragraphToken.tokens,
            markdownParser: input.markdownParser,
            evidenceEnabled: input.options.evidenceEnabled,
          })
        : paragraphToken.text}
    </Cmarkdownparagraph>
  );
}

function renderBlockquoteToken(input: RenderMarkdownTokenInput): ReactNode {
  const blockquoteToken = input.token as Tokens.Blockquote;
  return (
    <Cmarkdownblockquote
      key={input.key}
      contentWrapperStyle={input.options.blockquoteContentWrapperStyle}
      containerStyle={input.options.blockquoteContainerStyle}
      hebrew={startsWithHebrew(blockquoteToken.text)}
    >
      {blockquoteToken.tokens &&
        renderMarkdownTokens({
          tokens: blockquoteToken.tokens,
          markdownParser: input.markdownParser,
          options: input.options,
          depth: input.depth + 1,
        })}
    </Cmarkdownblockquote>
  );
}

function renderListToken(input: RenderMarkdownTokenInput): ReactNode {
  const listToken = input.token as Tokens.List;
  const listStart = resolveOrderedListStart({ listToken });
  return (
    <Cmarkdownlist
      key={input.key}
      ordered={listToken.ordered}
      depth={input.depth}
      style={input.depth === 0 ? input.options.listStyle : undefined}
    >
      {listToken.items.map((item, itemIndex) => {
        return renderListItem({
          item,
          itemIndex,
          keyPrefix: input.key,
          ordered: listToken.ordered,
          listStart,
          markdownParser: input.markdownParser,
          options: input.options,
          depth: input.depth,
        });
      })}
    </Cmarkdownlist>
  );
}

function renderParenListToken(input: RenderMarkdownTokenInput): ReactNode {
  if (!isParenListToken(input.token)) {
    return null;
  }

  return (
    <ParenListBlock
      key={input.key}
      items={input.token.items}
      keyPrefix={input.key}
      depth={input.depth}
      listStyle={input.options.listStyle}
      listItemStyle={input.options.listItemStyle}
      markdownParser={input.markdownParser}
      renderItemContent={(inlineInput) =>
        renderInlineContent({
          ...inlineInput,
          evidenceEnabled: input.options.evidenceEnabled,
        })
      }
    />
  );
}

function renderListItem(input: RenderListItemInput): ReactNode {
  return (
    <Cmarkdownlistitem
      key={`${input.keyPrefix}-item-${input.itemIndex}`}
      ordered={input.ordered}
      index={input.listStart + input.itemIndex}
      task={input.item.task}
      checked={input.item.checked}
      style={input.options.listItemStyle}
      hebrew={startsWithHebrew(input.item.text)}
    >
      {input.item.tokens &&
        renderMarkdownTokens({
          tokens: input.item.tokens,
          markdownParser: input.markdownParser,
          options: input.options,
          depth: input.depth + 1,
        })}
    </Cmarkdownlistitem>
  );
}

function resolveOrderedListStart({
  listToken,
}: {
  listToken: Tokens.List;
}): number {
  if (!listToken.ordered) {
    return 1;
  }

  return typeof listToken.start === "number" && listToken.start > 0
    ? listToken.start
    : 1;
}

function renderCodeToken(input: RenderMarkdownTokenInput): ReactNode {
  return renderCodeBlock({
    codeToken: input.token as Tokens.Code,
    key: input.key,
    options: input.options,
  });
}

function renderCodeBlock(input: RenderCodeBlockInput): ReactNode {
  const codeText = input.codeToken.text ?? "";
  const codeLang = getCodeLanguage(input.codeToken);
  if (isEmptyCodeFence({ codeText, codeLang })) {
    return null;
  }

  const candidateBlock = renderFirstCodeBlockCandidate({
    ...input,
    codeText,
    codeLang,
  });

  return candidateBlock ?? renderPlainCodeBlock(input);
}

function getCodeLanguage(codeToken: Tokens.Code): string {
  return codeToken.lang?.trim().toLowerCase() ?? "";
}

function isEmptyCodeFence(input: {
  codeText: string;
  codeLang: string;
}): boolean {
  return input.codeText.trim() === "" && input.codeLang === "";
}

const CODE_BLOCK_CANDIDATE_RENDERERS: CodeBlockCandidateRenderer[] = [
  renderMarkdownFenceCodeBlock,
  renderAttachedResultsCodeBlock,
  renderMapFenceCodeBlock,
  renderMermaidCodeTokenBlock,
];

function renderFirstCodeBlockCandidate(
  input: ResolvedCodeBlockInput,
): ReactNode | null {
  const candidateBlocks = CODE_BLOCK_CANDIDATE_RENDERERS.map((renderer) =>
    renderer(input),
  );
  return candidateBlocks.find(isRenderedCodeBlock) ?? null;
}

function isRenderedCodeBlock(node: ReactNode | null): node is ReactNode {
  return node != null;
}

function renderMarkdownFenceCodeBlock(
  input: ResolvedCodeBlockInput,
): ReactNode {
  if (!isMarkdownFenceLanguage(input.codeLang)) {
    return null;
  }

  return input.options.renderMarkdownFence({
    key: input.key,
    content: input.codeText,
  });
}

/** A persisted attach block: shared grammar read first, then the exhaustive
 * pillar registry. A malformed block stays visible as source code. */
function renderAttachedResultsCodeBlock(
  input: ResolvedCodeBlockInput,
): ReactNode {
  if (input.codeLang !== MarkdownCodeLanguage.AttachedResults) return null;
  const block = parseAttachResults(input.codeText);
  if (block === null) return renderPlainCodeBlock(input);
  const Card = attachedResultCardFor({ pillar: block.pillar });
  return <Card key={input.key} block={block} />;
}

/** The first-class ```map fence. The mermaid-fenced spelling is handled by the
 *  header pattern table instead, and is registered last there. */
function renderMapFenceCodeBlock(input: ResolvedCodeBlockInput): ReactNode {
  if (input.codeLang !== MarkdownCodeLanguage.Map) {
    return null;
  }

  return <Cmarkdownmap key={input.key} content={input.codeText} />;
}

function renderMermaidCodeTokenBlock(input: ResolvedCodeBlockInput): ReactNode {
  if (input.codeToken.lang !== MarkdownCodeLanguage.Mermaid) {
    return null;
  }

  return renderMermaidCodeBlock({
    key: input.key,
    content: input.codeText,
  });
}

function renderPlainCodeBlock(input: RenderCodeBlockInput): ReactNode {
  const language = input.codeToken.lang?.trim().toLowerCase();
  return (
    <Cmarkdowncode
      key={input.key}
      inline={false}
      language={language}
      concaved={input.options.concaved}
      defaultExpanded={language === MarkdownCodeLanguage.Text}
    >
      {input.codeToken.text}
    </Cmarkdowncode>
  );
}

function isMarkdownFenceLanguage(codeLang: string): boolean {
  // `text` is literal provider/model output, not nested Markdown. Parsing it
  // recursively drops XML/HTML-looking lines as unsupported raw-HTML tokens.
  return (
    codeLang === MarkdownFenceLanguage.Markdown ||
    codeLang === MarkdownFenceLanguage.Md
  );
}

function renderMermaidCodeBlock(
  input: RenderCodeVisualInput,
): ReactNode | null {
  const renderer = MERMAID_CODE_BLOCK_RENDERERS.find((candidate) =>
    candidate.pattern.test(input.content),
  );

  return renderer ? renderer.render(input) : null;
}

function renderPieChartCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownpiechart key={input.key} content={input.content} />;
}

function renderFlowchartCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownflowchart key={input.key} content={input.content} />;
}

function renderXyChartCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownxychart key={input.key} content={input.content} />;
}

function renderMindmapCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownmindmap key={input.key} content={input.content} />;
}

function renderGanttCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdowngantt key={input.key} content={input.content} />;
}

function renderTimelineCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdowntimeline key={input.key} content={input.content} />;
}

function renderKanbanCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownkanban key={input.key} content={input.content} />;
}

function renderSequenceCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownsequence key={input.key} content={input.content} />;
}

function renderTreemapCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdowntreemap key={input.key} content={input.content} />;
}

function renderQuadrantCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownquadrant key={input.key} content={input.content} />;
}

function renderMapCodeBlock(input: RenderCodeVisualInput): ReactNode {
  return <Cmarkdownmap key={input.key} content={input.content} />;
}

const MERMAID_RENDERER_BY_FORM: Record<
  MermaidVisualForm,
  (input: RenderCodeVisualInput) => ReactNode
> = {
  [MermaidVisualForm.Pie]: renderPieChartCodeBlock,
  [MermaidVisualForm.Flowchart]: renderFlowchartCodeBlock,
  [MermaidVisualForm.XyChart]: renderXyChartCodeBlock,
  [MermaidVisualForm.Mindmap]: renderMindmapCodeBlock,
  [MermaidVisualForm.Gantt]: renderGanttCodeBlock,
  [MermaidVisualForm.Timeline]: renderTimelineCodeBlock,
  [MermaidVisualForm.Kanban]: renderKanbanCodeBlock,
  [MermaidVisualForm.Sequence]: renderSequenceCodeBlock,
  [MermaidVisualForm.Treemap]: renderTreemapCodeBlock,
  [MermaidVisualForm.Quadrant]: renderQuadrantCodeBlock,
};

/** A header entry with no mermaid form is the CABAL map block — the only
 *  non-mermaid form a ```mermaid fence may carry. */
function resolveMermaidFenceRenderer(
  entry: MermaidFenceHeaderEntry,
): (input: RenderCodeVisualInput) => ReactNode {
  if (!entry.form) {
    return renderMapCodeBlock;
  }

  return MERMAID_RENDERER_BY_FORM[entry.form];
}

/**
 * Dispatch table. Patterns and their order live in `mermaid-visual-patterns.ts`
 * (React-free, so the guide-corpus assert routes fences through the exact same
 * rules and pins the ordering); `renderMermaidCodeBlock` resolves the first
 * match, so that order is the precedence rule and this table is derived from
 * it rather than restating it.
 *
 * Matching a pattern hands the component ownership of the block including its
 * failure — an unparseable body resolves to `CmarkdownvisualError`, never to a
 * vanished block. Content matching no pattern keeps its raw source.
 */
const MERMAID_CODE_BLOCK_RENDERERS: MermaidCodeBlockRenderer[] =
  MERMAID_FENCE_HEADER_ORDER.map((entry) => ({
    pattern: entry.pattern,
    render: resolveMermaidFenceRenderer(entry),
  }));

function renderMathBlockToken(input: RenderMarkdownTokenInput): ReactNode {
  if (!isMathBlockToken(input.token)) {
    return null;
  }

  return (
    <Cmarkdownmath
      key={input.key}
      latex={input.token.text}
      displayMode={input.token.displayMode}
    />
  );
}

function renderHorizontalRuleToken(input: RenderMarkdownTokenInput): ReactNode {
  return <Cmarkdownhr key={input.key} />;
}

function renderImageToken(input: RenderMarkdownTokenInput): ReactNode {
  const imageToken = input.token as Tokens.Image;
  return (
    <Cmarkdownimage
      key={input.key}
      src={imageToken.href}
      alt={imageToken.text}
      title={imageToken.title || undefined}
    />
  );
}

function renderTableToken(input: RenderMarkdownTokenInput): ReactNode {
  const tableToken = input.token as Tokens.Table;
  return (
    <Cmarkdowntable
      key={input.key}
      headerCells={renderTableCells({
        cells: tableToken.header,
        evidenceEnabled: input.options.evidenceEnabled,
        tableAlignments: tableToken.align,
        markdownParser: input.markdownParser,
      })}
      rows={tableToken.rows.map((row) => {
        return renderTableCells({
          cells: row,
          evidenceEnabled: input.options.evidenceEnabled,
          tableAlignments: tableToken.align,
          markdownParser: input.markdownParser,
        });
      })}
      textSize={input.options.textSize}
      textWeight={input.options.textWeight}
      textAlign={input.options.textAlign}
    />
  );
}

function renderSpaceToken(): ReactNode {
  return null;
}

function renderTextBlockToken(input: RenderMarkdownTokenInput): ReactNode {
  const textToken = input.token as Tokens.Text;
  return (
    <Cmarkdownparagraph
      key={input.key}
      textSize={input.options.textSize}
      textWeight={input.options.textWeight}
      textAlign={input.options.textAlign}
      textStyle={input.options.paragraphTextStyle}
      hebrew={startsWithHebrew(textToken.text)}
    >
      {textToken.tokens && textToken.tokens.length > 0
        ? renderInlineTokens({
            tokens: textToken.tokens,
            markdownParser: input.markdownParser,
            evidenceEnabled: input.options.evidenceEnabled,
          })
        : renderInlineContent({
            text: textToken.text,
            markdownParser: input.markdownParser,
            evidenceEnabled: input.options.evidenceEnabled,
          })}
    </Cmarkdownparagraph>
  );
}

function renderTableCells(input: {
  cells: Tokens.TableCell[];
  evidenceEnabled: boolean;
  tableAlignments: MarkdownTableAlignment[];
  markdownParser: Marked;
}): CmarkdowntableCell[] {
  return input.cells.map((cell, columnIndex) => ({
    content:
      cell.tokens && cell.tokens.length > 0
        ? renderInlineTokens({
            tokens: cell.tokens,
            markdownParser: input.markdownParser,
            evidenceEnabled: input.evidenceEnabled,
          })
        : cell.text,
    align: resolveTableCellAlignment({
      cell,
      columnIndex,
      tableAlignments: input.tableAlignments,
    }),
    hebrew: startsWithHebrew(cell.text),
  }));
}

function resolveTableCellAlignment(input: {
  cell: Tokens.TableCell;
  columnIndex: number;
  tableAlignments: MarkdownTableAlignment[];
}): MarkdownTableAlignment {
  if ("align" in input.cell && input.cell.align) {
    return input.cell.align;
  }

  return input.tableAlignments[input.columnIndex] ?? null;
}
